import React, { useState } from 'react';
import { GetAppRounded as DownloadIcon } from '@material-ui/icons';
import { IconButton, makeStyles, CircularProgress } from '@material-ui/core';
import Table, {
  tempFilteredRows,
  columnType,
} from '../../../components/ClearingTable/Table';
import Button from '../../../components/Buttons/Button';
import DateTimePicker from '../../../components/Textfields/DateTimePicker';
import AutoCompleteCorrespondent from '../../../components/AutoComplete/AutoCompleteCorrespondent';
import AutoCompleteAccountNo from '../../../components/AutoComplete/AutoCompleteAccountNo';
import AutoCompleteAccountName from '../../../components/AutoComplete/AutoCompleteAccountName';
import AutoCompleteMasterAccountNo from '../../../components/AutoComplete/AutoCompleteMasterAccountNo';
import AutoCompleteBranch from '../../../components/AutoComplete/AutoCompleteBranch';
import AutoCompleteRep from '../../../components/AutoComplete/AutoCompleteRep';
import Text from '../../../components/Typography/Text';
import ReportDownloadModal from '../../../components/Modals/ReportDownloadModal';
import {
  notifyInfo,
  notifyError,
  notifySuccess,
  notifyWarning,
} from '../../../components/Messages/Notification';
import { listDailyTradeConfirm } from '../../../services/DailyTradeConfirmService';
import { getSystemDate } from '../../../services/ProfileService';
import QueryParam from '../../../services/QueryParamService';
import { GridTitleTradeConfirm } from '../../../components/contants/GridTitle';
import { CustomBodyRenderAlign } from '../../../components/Table/CustomBodyRender';
import {
  downloadDailyTradeConfirmation,
  batchDownloadDailyTradeConfirmation,
} from '../../../services/DailyTradeConfirmService';
import color from '../../../assets/css/colors';
import fontFamily from '../../../assets/css/fonts';
import { formatPbDate } from '../../../lib/fmt';

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 800,
    width: '100%',
    margin: '0 auto',
    float: 'clear',
  },
  modalHeader: {
    backgroundColor: 'white',
    padding: 25,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    borderBottom: '1px solid #ccc',
    display: 'flex',
    '& div:nth-child(1)': {
      flex: 1,
    },
    '& div:nth-child(2)': {
      flex: 'none',
      '& .MuiIconButton-root': {
        borderRadius: 5,
        padding: 10,
        '&:hover svg path': {
          fill: color.primary.main,
        },
      },
      '& svg:last-child': {
        width: 15,
        height: 15,
      },
    },
  },
  modalBody: {
    backgroundColor: 'white',
    padding: '0px 25px 25px 25px',
    fontFamily: fontFamily.headers,
    '& p': {
      fontSize: 16,
      lineHeight: '24px',
    },
  },
  modalFooter: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    backgroundColor: 'white',
    padding: '0px 25px 25px 25px',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    position: 'relative',
    '& button': {
      width: 'auto',
    },
  },
  closeButton: {
    '& button': {
      marginTop: '-4px !important',
    },
  },
}));

export default function TradeConfirm() {
  const classes = useStyles();
  const [lockTable, setLockTable] = useState(true);
  const [pagination, setPagination] = useState({});
  const [previousRows, setPreviousRows] = useState(0);
  const [searchDataCsv, setSearchDataCsv] = React.useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [rows, setRows] = React.useState([]);
  const [open, setOpen] = React.useState(false);

  const [loading, setLoading] = React.useState({
    search: false,
    download: false,
  });

  const [searchData, setSearchData] = React.useState(
    QueryParam.get({
      fromDate: '',
      toDate: '',
      correspondent: '',
      branch: '',
      accountNo: '',
      masterAccountNo: '',
      rep: '',
      accountName: '',
    })
  );

  const [downloadData, setDownloadData] = React.useState({
    accountId: '',
    tradeDate: '',
  });

  const [currentSelectedRowsIndex, setCurrentSelectedRowsIndex] = React.useState([]);

  const init = async () => {
    const searchParameters = localStorage.getItem('search_data');

    const {
      fromDate,
      toDate,
      correspondent,
      branch,
      accountNo,
      masterAccountNo,
      rep,
      accountName,
    } = searchParameters ? JSON.parse(searchParameters) : searchData;

    const systemDate = await getSystemDate();

    setSearchData({
      ...searchData,
      fromDate: fromDate ? fromDate : systemDate,
      toDate: toDate ? toDate : systemDate,
      correspondent: correspondent ? correspondent : '',
      branch: branch ? branch : '',
      accountNo: accountNo ? accountNo : '',
      masterAccountNo: masterAccountNo ? masterAccountNo : '',
      rep: rep ? rep : '',
      accountName: accountName ? accountName : '',
    });
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line
  }, []);

  const columns = [
    {
      name: 'actions',
      label: 'Actions',
      type: columnType.buttons,
      options: {
        customBodyRenderLite: (dataIndex, rowIndex) => {
          return (
            <div align={'left'}>
              <div align={'left'} style={{ padding: 5 }}>
                <IconButton
                  aria-label="download"
                  disabled={loading.download}
                  onClick={() =>
                    handleDownloadTradeConfirm(
                      tempFilteredRows[dataIndex]?.tradeDate,
                      tempFilteredRows[dataIndex]?.accountId,
                      rowIndex
                    )
                  }
                >
                  {loading.download && currentSelectedRowsIndex.includes(dataIndex) ? (
                    <CircularProgress
                      style={{ color: '#1e7dff', height: 20, width: 20 }}
                    />
                  ) : (
                    <DownloadIcon />
                  )}
                </IconButton>
              </div>
            </div>
          );
        },
        sort: false,
      },
    },
    {
      // Already converted to string in handlesearch
      name: 'tradeDate',
      label: 'Trade Date',
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'branch',
      label: 'Branch',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountNo',
      label: 'Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'masterAccountNo',
      label: 'Master Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'rep',
      label: 'Rep/Advisor',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'trnsCount',
      label: 'TRNS Count',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    responsive: 'standard',
    isRowSelectable: () => !loading.download,
    customToolbarSelect: (selectedRows) => {
      return (
        <div className={classes.grdRow} style={{ marginRight: 30, padding: 0 }}>
          <div className={classes.grdCellNone}>
            <Button
              id="batch"
              disabled={loading.search}
              loading={loading.search}
              type="search"
              label={'BATCH DOWNLOAD'}
              onClick={() => {
                handleBatchDownloadTradeConfirm(
                  selectedRows.data,
                  selectedRows.data.map((d) => d.dataIndex)
                );
              }}
            />
          </div>
        </div>
      );
    },
    serverSide: true,
  };

  const handleSearchDataChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    setSearchData({ ...searchData, [input.name]: input.value });
  };

  const handleSearch = async (paginationChange) => {
    let valid = true;

    if (!searchData.fromDate) {
      notifyWarning('From Date is required.');
      valid = false;
    }

    if (!searchData.toDate) {
      notifyWarning('To Date is required.');
      valid = false;
    }

    if (!valid) {
      return;
    }
    try {
      setLoading({ ...loading, search: true });
      if (paginationChange !== true) {
        setLockTable(false);
      }
      if (paginationChange === true) {
        if (lockTable === true) {
          return;
        }
      }
      QueryParam.set(searchData);
      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };
      if (previousRows != 0) {
        if (previousRows < pagination.rowsPerPage * pagination.pageNo) {
          paginationCopy.pageNo = 0;
        }
      }
      if (paginationChange !== true) {
        paginationCopy.filterNames = [];
        paginationCopy.filterValues = [];
      }
      localStorage.setItem('search_data', JSON.stringify(searchData));

      const data = await listDailyTradeConfirm(searchData, paginationCopy);
      setSearchDataCsv(searchData);
      setPreviousRows(data.summary.totalRows);
      setTotalRows(data.summary.totalRows);
      paginationCopy.count = data.summary.totalRows;
      setPagination(paginationCopy);
      let rowsCopy = data.dailyTradeConfirmationsList;
      const rowsCopyOutput = rowsCopy.map((data) => {
        let tempDate = formatPbDate(data.tradeDate);
        data.tradeDate = tempDate;

        return data;
      });
      setRows(rowsCopyOutput);
      if (paginationChange !== true) {
        notifyInfo(data.summary.totalRows + ' search results.');
      }
    } catch (error) {
      setLoading({ ...loading, search: false });
      notifyError(error.message);
      console.log(error);
    } finally {
      setLoading({ ...loading, search: false });
    }
  };

  const handleDownloadTradeConfirm = async (tradeDate, accountId, dataIndex) => {
    setOpen(true);

    setDownloadData({
      accountId: accountId,
      tradeDate: tradeDate,
      dataIndex: dataIndex,
    });
  };

  const handleBatchDownloadTradeConfirm = async (selected, index) => {
    const params = [];

    selected.forEach((data) => {
      params.push({
        accountId: tempFilteredRows[data.dataIndex]?.accountId,
        tradeDate: tempFilteredRows[data.dataIndex]?.tradeDate,
      });
    });

    try {
      setCurrentSelectedRowsIndex(index);
      setLoading({ ...loading, download: true });
      await batchDownloadDailyTradeConfirmation(params);
      setLoading({ ...loading, download: false });
      notifySuccess('Daily Trade Confirm Download is Completed');
      setCurrentSelectedRowsIndex([]);
    } catch (error) {
      setCurrentSelectedRowsIndex([]);
      setLoading({ ...loading, download: false });
    }
  };

  const getCsvData = async () => {
    if (lockTable === true) {
      return [];
    }
    let paginationCopy = {
      ...pagination,
      reload: false,
      pageNo: 0,
      rowsPerPage: totalRows,
    };
    const data = await listDailyTradeConfirm(searchDataCsv, paginationCopy);
    let rowsCopy = data.dailyTradeConfirmationsList;
    const rowsCopyOutput = rowsCopy.map((data) => {
      let tempDate = formatPbDate(data.tradeDate);
      data.tradeDate = tempDate;

      return data;
    });
    return rowsCopy;
  };

  return (
    <form className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Daily Trade Confirm" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading.search}
              loading={loading.search}
              type="search"
              label={loading.search ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell-none">
            <DateTimePicker
              type="date"
              name="fromDate"
              label="From Date"
              value={searchData.fromDate}
              onChange={handleSearchDataChange}
              required={true}
              inputProps={{ max: searchData.toDate }}
            />
          </div>
          <div className="grd-cell-none">
            <DateTimePicker
              type="date"
              name="toDate"
              label="To Date"
              value={searchData.toDate}
              onChange={handleSearchDataChange}
              required={true}
              inputProps={{ min: searchData.fromDate }}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteCorrespondent
              name="correspondent"
              value={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteBranch
              name="branch"
              value={searchData.branch}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountNo
              name="accountNo"
              value={searchData.accountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteMasterAccountNo
              name="masterAccountNo"
              value={searchData.masterAccountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteRep
              name="rep"
              value={searchData.rep}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountName
              name="accountName"
              value={searchData.accountName}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
        </div>
      </div>
      <div className="mt-20">
        <Table
          title={'Daily Trade Confirm'}
          data={rows}
          columns={columns}
          options={options}
          getCsvData={getCsvData}
          pagination={pagination}
          setPagination={setPagination}
          onSearch={handleSearch}
        />
      </div>
      <ReportDownloadModal
        title={GridTitleTradeConfirm}
        selectedIds={[downloadData.accountId]}
        rows={tempFilteredRows}
        parameters={downloadData}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      />
    </form>
  );
}
